import { toaster } from "@/components/ui/toaster";
import { EstadosSolicitudMargenBruto } from "@/constants/enums/status/entity-status-ids";
import {
  GrossMarginRequestDetail,
  UpdateCultivoDto,
  getGrossMarginRequestDetail,
  patchGrossMarginRequest,
} from "@/services/gross-margin-requests.service";
import { getAxiosErrorMessage } from "@/utils/axios-error-message";
import { handlePermissionError } from "@/utils/permission-check";
import { AxiosError } from "axios";
import { Dispatch, SetStateAction } from "react";
import { NavigateFunction } from "react-router-dom";

interface FetchGrossMarginRequestDetailHandlerProps {
  id: string | undefined;
  setLoading: Dispatch<SetStateAction<boolean>>;
  setDetail: Dispatch<SetStateAction<GrossMarginRequestDetail | null>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const fetchGrossMarginRequestDetailHandler = async ({
  id,
  setLoading,
  setDetail,
  navigate,
  fetchPermissions,
}: FetchGrossMarginRequestDetailHandlerProps) => {
  try {
    setLoading(true);
    const response = await getGrossMarginRequestDetail(Number(id));
    setDetail(response);
  } catch (error) {
    const handled = await handlePermissionError(error, navigate, fetchPermissions);
    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(
          error as AxiosError,
          "No se pudo obtener el detalle de la solicitud.",
        ),
      });
    }
  } finally {
    setLoading(false);
  }
};

interface TomarGrossMarginRequestHandlerProps {
  id: number;
  setLoading: Dispatch<SetStateAction<boolean>>;
  onSuccess: () => Promise<void>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

/** Transiciona la solicitud de Pendiente → EnProceso (el ATA/RSA la toma para trabajar). */
export const tomarGrossMarginRequestHandler = async ({
  id,
  setLoading,
  onSuccess,
  navigate,
  fetchPermissions,
}: TomarGrossMarginRequestHandlerProps) => {
  try {
    setLoading(true);
    await patchGrossMarginRequest(id, { idEstado: EstadosSolicitudMargenBruto.EnProceso });
    toaster.create({ type: "success", title: "Solicitud tomada exitosamente." });
    await onSuccess();
  } catch (error) {
    const handled = await handlePermissionError(error, navigate, fetchPermissions);
    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(
          error as AxiosError,
          "No se pudo tomar la solicitud.",
        ),
      });
    }
  } finally {
    setLoading(false);
  }
};

interface SubmitGrossMarginResultsHandlerProps {
  id: number;
  cultivos: UpdateCultivoDto[];
  urlInforme?: string;
  notasInforme?: string;
  setLoading: Dispatch<SetStateAction<boolean>>;
  onSuccess: () => Promise<void>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

/**
 * Envía los resultados calculados y confirma la solicitud (EnProceso → Confirmada).
 * Lanza el error luego de mostrarlo para que el componente padre pueda reaccionar.
 */
export const submitGrossMarginResultsHandler = async ({
  id,
  cultivos,
  urlInforme,
  notasInforme,
  setLoading,
  onSuccess,
  navigate,
  fetchPermissions,
}: SubmitGrossMarginResultsHandlerProps) => {
  try {
    setLoading(true);
    await patchGrossMarginRequest(id, {
      idEstado: EstadosSolicitudMargenBruto.Confirmada,
      urlInforme,
      notasInforme,
      cultivos,
    });
    toaster.create({
      type: "success",
      title: "Solicitud confirmada exitosamente.",
    });
    await onSuccess();
  } catch (error) {
    const handled = await handlePermissionError(error, navigate, fetchPermissions);
    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(
          error as AxiosError,
          "No se pudo completar la solicitud.",
        ),
      });
    }
    throw error;
  } finally {
    setLoading(false);
  }
};
